import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const protections = [
  {
    icon: 'ri-lock-line',
    title: 'Right to Access',
    description: 'You have the right to access and obtain a copy of your health information that we maintain.',
  },
  {
    icon: 'ri-edit-line',
    title: 'Right to Amend',
    description: 'You may request corrections to your health information if you believe it is inaccurate or incomplete.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Right to an Accounting',
    description: 'You have the right to receive a list of certain disclosures we have made of your health information.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Right to Restrict',
    description: 'You may request restrictions on how we use or disclose your health information for treatment, payment, or operations.',
  },
  {
    icon: 'ri-mail-settings-line',
    title: 'Right to Confidential Communications',
    description: 'You may request that we communicate with you about your health information in a specific way or at a specific location.',
  },
  {
    icon: 'ri-close-circle-line',
    title: 'Right to Revoke Authorization',
    description: 'If you have given us authorization to use or disclose your health information, you may revoke that authorization in writing at any time.',
  },
];

const safeguards = [
  {
    icon: 'ri-server-line',
    title: 'Technical Safeguards',
    description: 'Encryption of data in transit and at rest, access controls, and audit logs to monitor access to protected health information.',
  },
  {
    icon: 'ri-building-line',
    title: 'Physical Safeguards',
    description: 'Controlled facility access, workstation security policies, and device and media controls to prevent unauthorized physical access.',
  },
  {
    icon: 'ri-team-line',
    title: 'Administrative Safeguards',
    description: 'Staff training on HIPAA requirements, designated privacy officer, risk assessments, and written policies and procedures.',
  },
  {
    icon: 'ri-contract-line',
    title: 'Business Associate Agreements',
    description: 'All third-party vendors who handle protected health information are required to sign Business Associate Agreements (BAAs) ensuring HIPAA compliance.',
  },
];

export default function HipaaNotice() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-hipaa';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'HIPAA Notice of Privacy Practices – AltaLumé ADHD Testing',
      url: `${siteUrl}/hipaa-notice`,
      description:
        'AltaLumé Mental Health HIPAA Notice of Privacy Practices. Understand your rights and how we protect your health information collected during IVA-2 ADHD attention testing.',
      inLanguage: 'en-US',
      isPartOf: {
        '@type': 'WebSite',
        name: 'AltaLumé ADHD Testing',
        url: siteUrl,
      },
      dateModified: '2026-03-11',
    });
    if (!document.getElementById('schema-hipaa')) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById('schema-hipaa');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-violet-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <i className="ri-shield-check-fill text-base"></i>
            HIPAA Compliance
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6 leading-tight">
            Notice of Privacy Practices
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            AltaLumé Mental Health is committed to protecting your health information in accordance with the Health Insurance Portability and Accountability Act (HIPAA).
          </p>
          <p className="text-sm text-stone-400 mt-4">Effective Date: March 11, 2026</p>
        </div>
      </section>

      <main className="pb-20">
        <div className="max-w-4xl mx-auto px-6">

          {/* Important Notice Banner */}
          <div className="my-10 bg-violet-600 rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">This Notice Describes How Medical Information About You May Be Used and Disclosed</h2>
                <p className="text-violet-100 leading-relaxed text-sm">
                  Please review it carefully. This notice applies to all health-related information collected through AltaLumé's ADHD testing services, including IVA-2 Continuous Performance Test results and associated reports.
                </p>
              </div>
            </div>
          </div>

          {/* What is HIPAA */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-4">What is HIPAA?</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              The Health Insurance Portability and Accountability Act of 1996 (HIPAA) is a federal law that establishes national standards to protect individuals' medical records and other personal health information. HIPAA gives patients important rights over their health information and sets limits on who can access it.
            </p>
            <p className="text-stone-700 leading-relaxed">
              AltaLumé Mental Health maintains administrative, technical, and physical safeguards designed to protect the confidentiality, integrity, and availability of protected health information (PHI) in accordance with HIPAA and applicable state privacy laws.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-4">How We May Use and Disclose Your Health Information</h2>
            <p className="text-stone-700 leading-relaxed mb-6">
              AltaLumé may use and disclose your protected health information for the following purposes without requiring your separate written authorization:
            </p>

            <div className="space-y-5">
              {[
                {
                  title: 'Testing & Service Delivery',
                  desc: 'We use your information to administer the IVA-2 Continuous Performance Test, generate your attention and response-control report, and deliver results to you.',
                },
                {
                  title: 'Healthcare Operations',
                  desc: 'We may use your information for internal operations such as quality assurance, staff training, and improving our testing services.',
                },
                {
                  title: 'Collaborative Care',
                  desc: 'With your consent, we may share your testing report with your primary care provider (PCP), psychiatrist, or other treating clinician to support a comprehensive evaluation.',
                },
                {
                  title: 'Legal & Regulatory Compliance',
                  desc: 'We may disclose your information as required by law, including in response to court orders, subpoenas, or requests from public health authorities.',
                },
                {
                  title: 'Business Associates',
                  desc: 'We may share information with trusted vendors (e.g., testing software platforms, data storage providers) who are contractually obligated to protect your information under a Business Associate Agreement.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 p-5 bg-stone-50 rounded-xl border border-stone-100">
                  <div className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0 mt-2"></div>
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">{item.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Uses Requiring Authorization */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-4">Uses Requiring Your Written Authorization</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              Other uses and disclosures of your health information not described in this notice will be made only with your written authorization, including:
            </p>
            <ul className="space-y-3">
              {[
                'Marketing communications',
                'Sale of your health information',
                'Disclosure to employers or insurance companies (unless required by law)',
                'Any use not otherwise permitted under HIPAA',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-stone-700 text-sm">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-violet-500 text-lg"></i>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-2">Your Rights Under HIPAA</h2>
            <p className="text-stone-600 mb-8 text-sm leading-relaxed">
              You have the following rights regarding your protected health information. To exercise any of these rights, please contact us using the information below.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {protections.map((item) => (
                <div key={item.title} className="p-6 bg-white border border-stone-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 flex items-center justify-center bg-violet-50 rounded-xl mb-4">
                    <i className={`${item.icon} text-violet-600 text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Safeguards */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-2">How We Protect Your Information</h2>
            <p className="text-stone-600 mb-8 text-sm leading-relaxed">
              AltaLumé implements comprehensive safeguards to ensure the security and confidentiality of your protected health information.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {safeguards.map((item) => (
                <div key={item.title} className="p-6 bg-gradient-to-br from-violet-50 to-white border border-violet-100 rounded-2xl">
                  <div className="w-10 h-10 flex items-center justify-center bg-violet-100 rounded-xl mb-4">
                    <i className={`${item.icon} text-violet-700 text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Breach Notification */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-4">Breach Notification</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              In the event of a breach of unsecured protected health information, AltaLumé will notify affected individuals without unreasonable delay and no later than 60 days following discovery of the breach, as required by the HIPAA Breach Notification Rule.
            </p>
            <p className="text-stone-700 leading-relaxed">
              Notification will be provided via the contact information you have on file with us. If the breach affects 500 or more individuals in a state or jurisdiction, we will also notify prominent media outlets and the Secretary of the U.S. Department of Health and Human Services (HHS).
            </p>
          </section>

          {/* Complaints */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-stone-900 mb-4">How to File a Complaint</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              If you believe your privacy rights have been violated, you may file a complaint with AltaLumé or with the U.S. Department of Health and Human Services Office for Civil Rights. We will not retaliate against you for filing a complaint.
            </p>
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100">
              <p className="text-stone-700 text-sm mb-1 font-semibold">To file a complaint with HHS:</p>
              <a
                href="https://www.hhs.gov/hipaa/filing-a-complaint/index.html"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-violet-600 hover:text-violet-700 text-sm underline cursor-pointer"
              >
                www.hhs.gov/hipaa/filing-a-complaint
              </a>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-stone-900 mb-4">Contact Our Privacy Officer</h2>
            <p className="text-stone-700 leading-relaxed mb-6">
              For questions about this notice, to exercise your rights, or to file a complaint, please contact us:
            </p>
            <div className="bg-violet-600 rounded-2xl p-8 text-white">
              <p className="text-xl font-bold mb-1">AltaLumé Mental Health</p>
              <p className="text-violet-200 text-sm mb-6">Privacy Officer</p>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-violet-200 text-base"></i>
                  </div>
                  <span className="text-violet-100">admin@altalume.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-violet-200 text-base"></i>
                  </div>
                  <span className="text-violet-100">www.altalume.com</span>
                </div>
              </div>
            </div>
          </section>

          {/* Footer links */}
          <div className="flex flex-wrap gap-4 pt-6 border-t border-stone-100">
            <Link to="/" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer">← Back to Home</Link>
            <Link to="/privacy" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer">Privacy Policy</Link>
            <Link to="/terms" className="text-sm text-stone-500 hover:text-violet-600 transition-colors cursor-pointer">Terms of Service</Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
